import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/Base/Dialog";
import { Button } from "@repo/ui/components/Base/Button";
import type { Meeting } from "@repo/types";
import React, { useState } from "react";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { agendaModuleRepo } from "@/repositories/agendaModuleRepo";
import { agendaRepo } from "@/repositories/agendaRepo";
import { meetingRepo } from "@/repositories/meetingRepo";

interface ArchiveMeetingConfirmationProps {
  open: boolean;
  setOpen: (arg: boolean) => void;
  tenantId: string;
  meeting: Meeting;
  onDeleted?: (item: Meeting | undefined) => void | Promise<void>;
  message?: React.ReactNode;
}

export function ArchiveMeetingConfirmation({
  open,
  setOpen,
  tenantId,
  meeting,
  onDeleted,
  message = (
    <>
      Deleting this meeting will also delete its agenda, all sections, and any
      agenda items selected below.
      <br />
      Select the items below that you would like to delete:
    </>
  ),
}: ArchiveMeetingConfirmationProps) {
  const [loading, setLoading] = useState(false);

  const handleOpenChange = () => setOpen(!open);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const handleConfirm = async () => {
    setLoading(true);

    const agenda = meeting.agenda;
    if (agenda) {
      for (const agendaModule of agenda.agendaModules ?? []) {
        for (const agendaItem of agendaModule.agendaItems ?? []) {
          // if item isn't selected, don't delete it
          if (!selectedItems.includes(agendaItem.id)) continue;

          // otherwise, delete items
          await agendaItemRepo.delete(tenantId, agendaItem.id);
        }

        // delete agenda modules
        await agendaModuleRepo.delete(tenantId, agendaModule.id);
      }

      // delete agenda
      await agendaRepo.delete(tenantId, agenda.id);
    }

    // delete meeting
    await meetingRepo.delete(tenantId, meeting.id);

    await onDeleted?.(meeting);
    setOpen(false);
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you sure?</DialogTitle>
        </DialogHeader>
        <DialogDescription>{message}</DialogDescription>
        <DialogBody>
          <ul className="my-2">
            {meeting.agenda?.agendaModules?.map((agendaModule) => (
              <li key={agendaModule.id} className="my-4">
                <h4 className="font-bold text-base">{agendaModule.name}</h4>
                <ul className="ml-5">
                  {agendaModule.agendaItems?.map((agendaItem) => {
                    const selected = selectedItems.includes(agendaItem.id);

                    return (
                      <li key={agendaItem.id}>
                        <div className="flex flex-row gap-4 items-center">
                          <CheckboxInput
                            containerStyle="w-fit"
                            orientation="horizontal"
                            checked={selected}
                            onCheckedChange={(checked) => {
                              const newItems = selectedItems.filter(
                                (item) => item !== agendaItem.id
                              );
                              if (checked) newItems.push(agendaItem.id);
                              setSelectedItems(newItems);
                            }}
                            label={
                              <>
                                {agendaItem.name}
                                {selected ? (
                                  <span className="text-destructive text-sm inline-block shrink-0">
                                    &nbsp; (Agenda item will be deleted)
                                  </span>
                                ) : null}
                              </>
                            }
                          />
                        </div>
                        <ul className="ml-5 my-2">
                          {agendaItem.documents?.map((document) => (
                            <li key={document.id} className="text-base">
                              {document.name}
                              {selected ? (
                                <span className="text-tertiary text-sm shrink-0">
                                  &nbsp; (Documents will be retained and not
                                  deleted)
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </DialogBody>
        <DialogFooter className="sm:justify-between">
          <Button
            onClick={handleOpenChange}
            variant="destructive"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={() => handleConfirm()} disabled={loading}>
            Confirm Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
